import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Clipboard, Truck, Grid3X3, Plus } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useAuth } from '../../hooks/useAuth';

interface MobileBottomNavProps {
  onMenuClick: () => void;
  isMenuOpen?: boolean;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  onMenuClick,
  isMenuOpen = false,
}) => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = Number(user?.roleId) === 1 || Number(user?.roleId) === 3;
  const isB2B = location.pathname.startsWith('/b2b');

  const bottomNavItems = React.useMemo(() => {
    if (isB2B) {
      if (isAdmin) {
        return [
          { id: 'dashboard', label: 'Home', path: '/b2b/dashboard', icon: Home },
          { id: 'orders', label: 'All orders', path: '/b2b/orders', icon: Clipboard },
          { id: 'add-orders', label: 'Add order', path: '/b2b/orders/add', icon: Plus },
          { id: 'menu', label: 'More', path: '#', icon: Grid3X3 },
        ];
      }
      return [
        { id: 'dashboard', label: 'Home', path: '/b2b/dashboard', icon: Home },
        { id: 'orders', label: 'All orders', path: '/b2b/orders', icon: Clipboard },
        { id: 'menu', label: 'More', path: '#', icon: Grid3X3 },
      ];
    }

    return [
      { id: 'dashboard', label: 'Home', path: '/dashboard', icon: Home },
      { id: 'orders', label: 'All orders', path: '/orders', icon: Clipboard },
      { id: 'remittance', label: 'Remittance', path: '/finance/remittance', icon: Truck },
      { id: 'menu', label: 'More', path: '#', icon: Grid3X3 },
    ];
  }, [isB2B, isAdmin]);

  return (
    <nav
      className={cn(
        'lg:hidden w-auto mx-2 fixed shadow shadow-xl bottom-4 left-0 right-0 rounded-full backdrop-blur-sm border-t border-border safe-area-inset-bottom z-40 transition-all duration-300',
        isMenuOpen ? 'translate-y-20 opacity-0 pointer-events-none' : 'bg-mobileNav translate-y-0 opacity-100'
      )}
    >
      <div className="flex items-center justify-around h-16 py-4">
        {bottomNavItems.map((item) => {
          const Icon = item.icon;
          const isMenu = item.id === 'menu';
          const isActive =
            !isMenu &&
            (location.pathname === item.path ||
              (item.id === 'orders' &&
                location.pathname.startsWith(item.path) &&
                location.pathname !== '/b2b/orders/add' &&
                location.pathname !== '/orders/add'));

          // Common color classes
          const iconAndTextColor = isMenuOpen ? 'text-teal' : 'text-white';

          if (isMenu) {
            return (
              <button
                key={item.id}
                onClick={onMenuClick}
                className={cn(
                  'flex flex-col items-center justify-center flex-1 p-2 transition-all duration-200',
                  isMenuOpen ? 'scale-105' : 'hover:scale-105 active:scale-95',
                  iconAndTextColor
                )}
              >
                <Icon className={cn('h-5 w-5 mb-1', iconAndTextColor)} />
                <span className={cn('text-sm font-semibold', iconAndTextColor)}>
                  {item.label}
                </span>
              </button>
            );
          }

          return (
            <Link
              key={item.id}
              to={item.path}
              className={cn(
                'flex flex-col items-center justify-center flex-1 p-2 transition-all duration-200',
                isActive ? 'scale-105' : 'hover:scale-105 active:scale-95',
                iconAndTextColor
              )}
            >
              <Icon className={cn('h-5 w-5 mb-1', iconAndTextColor)} />
              <span className={cn('text-sm font-semibold', iconAndTextColor)}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
